import {
	FormConfig,
	CustomFormAccessor,
} from 'src/shared/form-models/form-cva.component';
import {
	AbstractControl,
	NgControl,
	FormBuilder,
	FormControl,
	Validators,
} from '@angular/forms';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import {
	takeUntil,
	filter,
	map,
	startWith,
	distinctUntilChanged,
	switchMap,
	shareReplay,
	take,
} from 'rxjs/operators';
import { JobLocationPartialState } from './+state/job-location.reducer';
import { Store, select } from '@ngrx/store';
import * as JobLocationActions from './+state/job-location.actions';
import * as JobLocationSelectors from './+state/job-location.selectors';
import { displayAddress } from './job-location.utility';
import {
	displayName,
	idComparer,
	touchControl,
} from 'src/shared/utilities/utility-functions';
import { TypeAheadService } from '../services/type-ahead.service';
import { LookUpsService } from '../lookups.service';
import { LocationDetails } from './job-location.models';
import { JobCurrency, Subdivision } from '../api';
import { DEFAULT_COUNTRY_CODE, isoCodeForCurrencyType } from '../constants';

const jobLocationFormConfig: FormConfig<LocationDetails> = {
	latitude: [''],
	longitude: [''],
	startingMilePost: [''],
	endingMilePost: [''],
	distanceFromTracks: [''],
	subdivision: [''],
	location: [''],
	countryCode: [DEFAULT_COUNTRY_CODE, [Validators.required]],
	currency: [JobCurrency.Usd, [Validators.required]],
	city: ['', [Validators.required]],
	// The state value must be a region of the selected country; that list comes from the API,
	// so the check is an async validator attached in ngOnInit.
	state: ['', [Validators.required]],
};

@Component({
	selector: 'rpshared-job-location',
	templateUrl: './job-location.component.html',
})
export class JobLocationComponent
	extends CustomFormAccessor<LocationDetails>
	implements OnInit, OnDestroy
{
	@Input() subdivisions: Subdivision[] = [];
	@Input() showCurrencySelect = false;

	readonly displayAddress = displayAddress;
	readonly idComparer = idComparer;
	readonly displayName = displayName;

	addressSearch = new FormControl('') as FormControl;
	latlng = new FormControl('') as FormControl;

	readonly filteredAddresses$ = this.typeAheadService.filteredAddresses$(
		this.addressSearch.valueChanges
	);

	readonly countries$ = this.lookupsService.countries;

	// Fed by BOTH country sources: user edits (valueChanges, wired in ngOnInit) and writeValue —
	// the load path patches the form with emitEvent: false, so valueChanges alone never sees the
	// loaded job's country and the state list would stay on the US default.
	private readonly _countryCode = new BehaviorSubject<string>(
		DEFAULT_COUNTRY_CODE
	);

	readonly regions$ = this._countryCode.pipe(
		map((code) => code || DEFAULT_COUNTRY_CODE),
		distinctUntilChanged(),
		switchMap((code) => this.lookupsService.regionsForCountry(code)),
		shareReplay(1)
	);

	readonly currencyChoices = Object.values(JobCurrency).map((currency) => ({
		currency,
		code: isoCodeForCurrencyType(currency),
	}));

	filteredStates$ = combineLatest([
		this.regions$,
		this.form.get('state').valueChanges.pipe(
			startWith(''),
			map((s) => (s ? s.toLowerCase() : ''))
		),
	]).pipe(
		map(([regions, s]) =>
			regions
				.filter((region) =>
					region.regionCode.toLowerCase().includes(s)
				)
				.sort(
					(r1, r2) =>
						Number(r2.regionCode.toLowerCase().startsWith(s)) -
						Number(r1.regionCode.toLowerCase().startsWith(s))
				)
				.map((region) => ({
					code: region.regionCode,
					name: region.regionName,
				}))
		)
	);

	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder,
		private readonly typeAheadService: TypeAheadService,
		private readonly lookupsService: LookUpsService,
		private readonly store: Store<JobLocationPartialState>
	) {
		super(ngControl, fb, jobLocationFormConfig);
	}

	onCountryChanged() {
		this.form.get('state').setValue('');

		const countryCode = this.form.get('countryCode').value;
		this.countries$.pipe(take(1)).subscribe((countries) => {
			const country = (countries || []).find(
				(c) => c.countryCode === countryCode
			);
			if (country) {
				this.form
					.get('currency')
					.setValue(
						JobCurrency[
							country.currency as keyof typeof JobCurrency
						]
					);
			}
		});
	}

	private readonly isValidRegionOfSelectedCountry = (
		control: AbstractControl
	) => {
		if (!control.value) {
			return of(null);
		}
		// Resolved by the form's current country rather than regions$: the shared stream can
		// momentarily replay the previous country's list while a load switches countries.
		const countryCode =
			this.form.get('countryCode').value || DEFAULT_COUNTRY_CODE;
		return this.lookupsService.regionsForCountry(countryCode).pipe(
			take(1),
			map((regions) =>
				regions.some(
					(region) =>
						region.regionCode.toUpperCase() ===
						String(control.value).toUpperCase()
				)
					? null
					: { invalidItem: true }
			)
		);
	};

	ngOnInit() {
		this.form
			.get('state')
			.setAsyncValidators(this.isValidRegionOfSelectedCountry);

		this.form
			.get('countryCode')
			.valueChanges.pipe(takeUntil(this.destroyed))
			.subscribe((code) => this._countryCode.next(code));

		this.latlng.valueChanges
			.pipe(takeUntil(this.destroyed))
			.subscribe((latlng) => {
				this.store.dispatch(JobLocationActions.pinDropped({ latlng }));
			});

		this.store
			.pipe(
				select(JobLocationSelectors.getLatLng),
				filter<{ longitude: number; latitude: number }>(Boolean),
				takeUntil(this.destroyed)
			)
			.subscribe((latlng) => {
				this.form.patchValue(latlng);
			});

		this.form.valueChanges
			.pipe(
				map(({ latitude, longitude }) => ({
					latitude,
					longitude,
				}))
			)
			.subscribe((latlng) => {
				this.latlng.setValue(latlng, { emitEvent: false });
			});

		super.ngOnInit();
	}

	// model --> view
	writeValue(value: LocationDetails): void {
		if (value) {
			const { latitude, longitude } = value;
			this.latlng.setValue({ latitude, longitude }, { emitEvent: false });
			super.writeValue(value);
			this._countryCode.next(value.countryCode || DEFAULT_COUNTRY_CODE);
		}
	}

	// view --> model
	registerOnChange(fn: (value: LocationDetails) => void) {
		super.registerOnChange(fn);
	}

	setDisabledState(isDisabled: boolean) {
		isDisabled ? this.form.disable() : this.form.enable();
	}

	selectLocation(address: { lat: number; lon: number }) {
		this.store.dispatch(JobLocationActions.locationSelected({ address }));
	}

	ngOnDestroy() {
		this.store.dispatch(JobLocationActions.jobLocationDestroyed());
		super.ngOnDestroy();
	}
	registerOnTouched(fn: () => void) {
		this.onTouched = touchControl(this.form.controls['state'])(fn);
	}

	onTouched: () => void = () => {};
}
